import {
	ChevronDown16Regular,
	Dialpad28Regular,
	Info20Filled,
} from "@fluentui/react-icons";
import { Divider, mergeClasses, tokens } from "@fluentui/react-components";
import { Flex, GapSize } from "@/components/atoms/flex";
import { useComponentStyle } from "./style";
import { InteractionDetailsProps as InteractionSourceDetailsProps } from "./types";
import { CustomerHistoryMediaType, InteractionType } from "@/common/types";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { WrapUpState, useWrapUpStore } from "@/common/stores/nonpersistingStores/wrapup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppCacheState, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { PopoverView } from "@/components/atoms/popover";
import { Dialpad, DialpadItem } from "@/components/molecules/dialpad";
import { isValidToneKeyNumber } from "@/common/helpers/validationHelper";
import { sendTone } from "@/repository/queryFns";
import { useIntl } from "react-intl";
import { CustomerInteractionHistory } from "@/components/organisms/customerHistory";
import { useCustomerHistory } from "@/hooks/customerHistory";
import { InteractionHistoryCustomerUpdated } from "@/components/organisms/customerHistory/types";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customerHistoryQueryKey } from "@/repository/queryFns/customerHistory";
import { useVariableTextVisibility } from "@/hooks/variableText";
import { InteractionSource } from "@/components/molecules/interactionSource";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import { IconSize } from "@/components/molecules/interactionComposedIcon/types";
import { getInteractionIcon } from "@/common/helpers/interactionIconHelper";
import { getInteractionSecondaryInfo } from "@/common/helpers/supervisorHelper";
import { AgentInteractions, InteractionDto } from "@/repository/models";
import {
	fetchInteractionsFn,
	interactionsQueryKey,
} from "@/repository/queryFns/interactions";
import { getInteractionName } from "@/common/helpers";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

export const InteractionBarMainInfo = (
	props: InteractionSourceDetailsProps
) => {
	const { type, isVariableTextOpen, setIsVariableTextOpen } = props;
	const intl = useIntl();
	const interactionBarCommonStyle = useInteractionBarCommonStyle();
	const componentStyle = useComponentStyle();
	const [isDialpadOpen, setIsDialpadOpen] = useState<boolean>(false);
	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);

	const queryClient = useQueryClient();

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) =>
			state.preview ? state.preview : state.active
	);

	const { data: interactions } = useQuery<AgentInteractions>({
		queryKey: [interactionsQueryKey],
		queryFn: fetchInteractionsFn,
	});

	const currentInteractionDto = useMemo(() => {
		const activeInteraction = interactions?.active?.find(
			(x) => x.id == currentInteraction?.id
		);

		if (activeInteraction) {
			return activeInteraction;
		}

		const waitingInteractionPreview = interactions?.waiting?.find(
			(x) => x.id == currentInteraction?.id
		);

		if (waitingInteractionPreview) {
			return waitingInteractionPreview;
		}

		return {
			id: currentInteraction?.id,
			type: currentInteraction?.type,
		} as InteractionDto;
	}, [interactions, currentInteraction]);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const { customerHistoryType } = useCustomerHistory();

	const isVariableTextVisible = useVariableTextVisibility();

	const { isMobileMedium } = useResponsiveBreakpoints();

	useEffect(() => {
		setCustomerHistoryPopupOpen(false);
	}, [currentInteraction?.id, wrapUp]);

	useEffect(() => {
		if (isMobileMedium) {
			setCustomerHistoryPopupOpen(false);
		}
	}, [isMobileMedium]);

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	notificationListener<InteractionHistoryCustomerUpdated>(
		ServerEvent.InteractionHistoryCustomerUpdated,
		(interactionHistoryCustomerUpdated: InteractionHistoryCustomerUpdated) => {
			if (
				currentInteraction?.id !==
				interactionHistoryCustomerUpdated.interactionId
			) {
				return;
			}

			setTimeout(() => {
				queryClient.invalidateQueries({
					queryKey: [
						customerHistoryQueryKey,
						customerHistoryType,
						currentInteraction.id,
					],
				});

				if (customerHistoryType === CustomerHistoryMediaType.WhatsApp) {
					queryClient.invalidateQueries({ queryKey: ["WhatsAppMessages"] });
				}

				if (customerHistoryType === CustomerHistoryMediaType.Webchat) {
					queryClient.invalidateQueries({ queryKey: ["WebchatMessages"] });
				}
			}, 1000); //elastic save is actually triggering an indexing operation, which will not be immediately reflected if retrieving the contact information again
		}
	);

	const interaction = useMemo(() => {
		if (!!wrapUp && wrapUp.mediaId === currentInteraction?.id) {
			const wrapUpInfo = appStoreCache[CacheKey.WrapUpInteractionInfo];

			return {
				name: currentInteraction?.name || wrapUpInfo?.name,
				contactInformation: currentInteraction?.contactInformation || wrapUpInfo?.contactInformation,
				description:
					type === InteractionType.Generic && wrapUpInfo?.description,
			};
		}

		if (type !== InteractionType.Call) {
			return {
				name: currentInteraction?.name,
				contactInformation: currentInteraction?.contactInformation,
				description:
					type === InteractionType.Generic && currentInteraction?.description,
			};
		}

		return {
			name: callSession ? callSession?.caller?.name : currentInteraction?.name,
			contactInformation: callSession
				? callSession?.caller?.contactInformation
				: currentInteraction?.contactInformation,
			description: "",
		};
	}, [wrapUp, type, currentInteraction, callSession, appStoreCache]);

	// Second line: contact info when available, otherwise the per-type generic
	// label, matching the interaction lists.
	const secondaryInfo = useMemo(
		() =>
			getInteractionSecondaryInfo(
				{
					type,
					name: interaction.name,
					contactInformation: interaction.contactInformation,
				},
				intl
			),
		[interaction, type, intl]
	);

	const onDialpadClick = (data: DialpadItem) => {
		if (!isValidToneKeyNumber(data.digit.toString()) || !callSession) {
			return;
		}

		if (!callSession.transferTarget || !callSession.transferTarget.name) {
			sendTone(data.digit);
		}
	};

	const [customerHistoryPopupOpen, setCustomerHistoryPopupOpen] =
		useState<boolean>(false);

	const isCustomerJourneyAvailable = useMemo(() => {
		return (
			currentInteraction?.type != InteractionType.Generic &&
			(currentInteraction?.contactInformation ||
				currentInteraction?.type != InteractionType.Webchat) &&
			!isMobileMedium
		);
	}, [currentInteraction, interaction, isMobileMedium]);

	const toggleCustomerHistoryPopoverOpening = useCallback(() => {
		if (!isCustomerJourneyAvailable) {
			return;
		}
		setCustomerHistoryPopupOpen((oldState) => !oldState);
	}, [
		setCustomerHistoryPopupOpen,
		wrapUp,
		currentInteraction?.id,
		isCustomerJourneyAvailable,
		isMobileMedium,
	]);

	const onDialpadIconClick = useCallback(() => {
		setIsDialpadOpen(!isDialpadOpen);
	}, [isDialpadOpen]);

	const onDialpadClose = useCallback(() => {
		setIsDialpadOpen(false);
	}, []);

	const isMonitoredInteraction =
		(currentInteractionDto?.id === callSession?.id &&
			callSession?.supervisorId !== null) ||
		currentInteractionDto?.isSupervised ||
		!!currentInteractionDto?.monitoringSupervisorId;

	return (
		<Flex
			vAlign="center"
			className={mergeClasses(
				interactionBarCommonStyle.interactionText,
				componentStyle.root
			)}
		>
			<Flex
				vAlign="center"
				className={mergeClasses(
					interactionBarCommonStyle.interactionIcon,
					interactionBarCommonStyle.iconCircle,
					wrapUp && wrapUp.mediaId === currentInteraction?.id
						? componentStyle.interactionWrapUpIconColor
						: interactionBarCommonStyle.interactionIconColor
				)}
			>
				{currentInteractionDto &&
					getInteractionIcon(currentInteractionDto, {
						isMonitoredInteraction,
						iconSize: IconSize.Large,
						hideDirectionArrow: true,
					})}
			</Flex>
			{callSession && !wrapUp && callSession?.id === currentInteraction?.id && (
				<div
					className={mergeClasses(
						componentStyle.interactionInfoContainer,
						componentStyle.dialpadButton
					)}
				>
					<PopoverView
						positioning={{
							position: "below",
							align: "start",
							offset: { crossAxis: -60, mainAxis: 20 },
						}}
						trigger={{
							content: (
								<Dialpad28Regular
									primaryFill={tokens.colorNeutralForeground1}
									onClick={onDialpadIconClick}
								/>
							),
							title: intl.formatMessage({
								id: "Interaction.New.DisabledDialpadMessage",
							}),
							titleVisibility: "onDisabled",
						}}
						content={
							<Dialpad
								onClick={onDialpadClick}
								onClose={onDialpadClose}
								isOutbound={false}
							/>
						}
						open={isDialpadOpen}
					/>
				</div>
			)}
			<PopoverView
				classes={{ trigger: componentStyle.minWidthZero }}
				positioning={{
					overflowBoundaryPadding: 16,
					position: "below",
					align: "start",
					offset: {
						// The icon circle is 10px wider than the old glyph, which shifts
						// this trigger right by the same amount, so pull the popover back.
						crossAxis: type == InteractionType.Call && !wrapUp ? -110 : -70,
						mainAxis: secondaryInfo ? 14 : 22,
					},
				}}
				open={customerHistoryPopupOpen}
				trigger={
					<Flex
						onClick={() => toggleCustomerHistoryPopoverOpening()}
						className={componentStyle.minWidthZero}
						gap={GapSize.ExtraLarge}
					>
						<InteractionSource
							mainInfo={getInteractionName(type, interaction.name, intl)}
							secondaryInfo={secondaryInfo}
							tertiaryInfo={interaction.description}
							direction={currentInteractionDto?.direction}
						/>
						{isCustomerJourneyAvailable && (
							<Flex vAlign="center" className={componentStyle.arrow}>
								<ChevronDown16Regular />
							</Flex>
						)}
					</Flex>
				}
				content={
					<CustomerInteractionHistory
						interactionId={currentInteraction?.id!}
						customerHistoryMediaType={customerHistoryType!}
						closePopup={toggleCustomerHistoryPopoverOpening}
					/>
				}
			/>
			<Divider className={interactionBarCommonStyle.divider} vertical />
			{isVariableTextVisible && !isVariableTextOpen && (
				<Info20Filled
					className={componentStyle.infoIcon}
					onClick={() => {
						setIsVariableTextOpen(true);
					}}
				/>
			)}
		</Flex>
	);
};
